import { ColumnWidths, DataTableColumn } from '../types';

/**
 * Calculates the left position for a sticky column based on checkbox selection
 * and the widths of all previous sticky columns.
 * 
 * @param column - The column to calculate position for
 * @param columns - All columns in the data grid
 * @param columnIndex - The index of the current column
 * @param columnWidths - The current column widths
 * @param checkboxSelection - Whether checkbox selection is enabled
 * @param dense - Whether the table is in dense mode (affects checkbox column width)
 * @returns The left position in pixels for the sticky column
 */
export const calculateStickyColumnLeft = <T = any>(
    column: DataTableColumn<T>,
    columns: DataTableColumn<T>[],
    columnIndex: number,
    columnWidths: ColumnWidths,
    checkboxSelection: boolean,
    dense: boolean = false
): number => {
    if (!column.sticky) {
        return 0;
    }

    let stickyLeft = 0;

    // Start with checkbox column width if present (including padding)
    if (checkboxSelection) {
        stickyLeft += dense ? 54 : 58;
    }

    // Add width of all previous sticky columns
    for (let i = 0; i < columnIndex; i++) {
        const prevColumn = columns[i];
        if (prevColumn.sticky) {
            const prevWidth = getColumnWidth(prevColumn, columnWidths);
            stickyLeft += prevWidth;
        }
    }

    return stickyLeft;
};

/**
 * Gets the width of a column, considering various width sources
 * 
 * @param column - The column to get width for
 * @param columnWidths - The current column widths
 * @returns The width in pixels
 */
export const getColumnWidth = <T = any>(
    column: DataTableColumn<T>,
    columnWidths: ColumnWidths
): number => {
    const width = columnWidths[column.id] ||
        (typeof column.width === 'number' ? column.width : null) ||
        (typeof column.minWidth === 'number' ? column.minWidth : null) ||
        150;

    return typeof width === 'number' ? width : 150;
};

/**
 * Checks if any columns in the array have sticky property enabled
 *
 * @param columns - Array of columns to check
 * @returns True if at least one column is sticky
 */
export const hasStickyColumns = <T = any>(columns: DataTableColumn<T>[]): boolean => {
    return columns.some(col => col.sticky);
};

/**
 * Calculates the right position for a sticky-right column based on the widths of
 * all subsequent sticky-right columns.
 *
 * @param column - The column to calculate position for
 * @param columns - All columns in the data grid
 * @param columnIndex - The index of the current column
 * @param columnWidths - The current column widths
 * @returns The right position in pixels for the sticky-right column
 */
export const calculateStickyColumnRight = <T = any>(
    column: DataTableColumn<T>,
    columns: DataTableColumn<T>[],
    columnIndex: number,
    columnWidths: ColumnWidths
): number => {
    if (!column.stickyRight) {
        return 0;
    }

    let stickyRight = 0;

    // Add width of all subsequent sticky-right columns
    for (let i = columnIndex + 1; i < columns.length; i++) {
        const nextColumn = columns[i];
        if (nextColumn.stickyRight) {
            stickyRight += getColumnWidth(nextColumn, columnWidths);
        }
    }

    return stickyRight;
};

/**
 * Checks if any columns in the array have stickyRight property enabled
 *
 * @param columns - Array of columns to check
 * @returns True if at least one column is sticky-right
 */
export const hasStickyRightColumns = <T = any>(columns: DataTableColumn<T>[]): boolean => {
    return columns.some(col => col.stickyRight);
};
